import { css } from '@emotion/core'
import { graphql, useStaticQuery } from 'gatsby'
import { GatsbyImage, IGatsbyImageData } from 'gatsby-plugin-image'
import parse from 'html-react-parser'
import React from 'react'

import { breakpoints } from '../constants/breakPoints'
import { isMobile } from '../helpers/common'
import { useTranslation } from '../lang/index'

interface PartnersProps {
  className?: string
  customLogos?: LogoImageProps
  partnersText?: string
  isOnIntro?: boolean
}

interface LogoImageProps {
  [name: string]: {
    childImageSharp: {
      gatsbyImageData: IGatsbyImageData
    }
  }
}

const Partners: React.FunctionComponent<PartnersProps> = ({
  className,
  customLogos,
  partnersText,
  isOnIntro = false,
}) => {
  const { t } = useTranslation()

  const logos: LogoImageProps =
    customLogos ??
    useStaticQuery(graphql`
      {
        ricoh: file(relativePath: { eq: "partners/ricoh.webp" }) {
          childImageSharp {
            gatsbyImageData(height: 17, layout: FIXED, placeholder: NONE)
          }
        }
        wago: file(relativePath: { eq: "partners/wago.webp" }) {
          childImageSharp {
            gatsbyImageData(height: 28, layout: FIXED, placeholder: NONE)
          }
        }
        cat: file(relativePath: { eq: "partners/cat.webp" }) {
          childImageSharp {
            gatsbyImageData(height: 28, layout: FIXED, placeholder: NONE)
          }
        }
        goodyear: file(relativePath: { eq: "partners/goodyear.webp" }) {
          childImageSharp {
            gatsbyImageData(height: 27, layout: FIXED, placeholder: NONE)
          }
        }
        ms: file(relativePath: { eq: "partners/ms.webp" }) {
          childImageSharp {
            gatsbyImageData(height: 34, layout: FIXED, placeholder: NONE)
          }
        }
        skanska: file(relativePath: { eq: "partners/skanska.webp" }) {
          childImageSharp {
            gatsbyImageData(height: 16, layout: FIXED, placeholder: NONE)
          }
        }
      }
    `)

  const orderedLogos = isMobile(breakpoints.small)
    ? ['wago', 'goodyear', 'ms', 'ricoh', 'skanska', 'cat']
    : isMobile(breakpoints.large)
    ? ['wago', 'ricoh', 'skanska', 'cat', 'goodyear', 'ms']
    : Object.keys(logos)

  return (
    <div id="partners-block" className={`${className} center`}>
      <div className="heading4 section center" css={subtitleStyle}>
        {(partnersText && parse(partnersText)) || parse(t('Partners.text'))}
      </div>
      <div css={containerStyle} id="partners-container-block">
        {orderedLogos.map((name) => {
          const logo = logos[name]
          return (
            <GatsbyImage
              image={logo.childImageSharp.gatsbyImageData}
              className={name}
              key={name}
              alt={name}
              imgStyle={{ objectFit: 'initial' }}
              loading={isOnIntro ? 'eager' : 'lazy'}
            />
          )
        })}
      </div>
    </div>
  )
}

export default Partners

const containerStyle = css`
  margin: 0 auto;
  display: flex;
  justify-content: center;
  align-items: center;
  gap: 2.75rem;
  opacity: 0.5;
  flex-wrap: wrap;

  @media screen and (max-width: ${breakpoints.large}px) {
    max-width: 40rem;
    gap: 2rem 3rem;
  }

  @media screen and (max-width: ${breakpoints.small}px) {
    max-width: 22rem;
    gap: 2rem 1.75rem;
  }
`

const subtitleStyle = css`
  margin: 4rem auto 2.5rem !important;
  text-align: center;
`
